import { useEffect, useState } from 'react';
import { updateConsentDenied, updateConsentGranted } from '../../lib/gtag';

type ConsentStatus = 'accepted' | 'rejected' | 'unset';

const STORAGE_KEY = 'abschlepp1-cookie-consent-v1';

function getInitialStatus(): ConsentStatus {
  if (typeof window === 'undefined') return 'unset';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'accepted' || stored === 'rejected') {
      return stored as ConsentStatus;
    }
  } catch {
    // Fallback, wenn localStorage geblockt ist
  }
  return 'unset';
}


export default function CookieBanner() {
  const [status, setStatus] = useState<ConsentStatus>('unset');
  const isVisible = status === 'unset';

  useEffect(() => {
    const initial = getInitialStatus();
    setStatus(initial);
    if (initial === 'accepted') {
      updateConsentGranted();
    } else if (initial === 'rejected') {
      updateConsentDenied();
    }
  }, []);

  const handleAccept = () => {
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(STORAGE_KEY, 'accepted');
      } catch {
        // ignore storage errors
      }
    }
    setStatus('accepted');
    updateConsentGranted();
  };

  const handleReject = () => {
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(STORAGE_KEY, 'rejected');
      } catch {
        // ignore storage errors
      }
    }
    setStatus('rejected');
    updateConsentDenied();
  };

  // Wenn noch keine Entscheidung getroffen wurde: großer Overlay-Dialog
  if (isVisible) {
    return (
      <div role="dialog" aria-modal="true" className="fixed inset-0 z-[9999] flex items-end justify-center bg-[#020617]/70 px-3 py-3 sm:items-center sm:px-4 sm:py-4">
        <div className="w-full rounded-3xl border-2 border-slate-300 bg-white p-5 text-slate-950 shadow-[0_28px_90px_rgba(2,6,23,0.45)] sm:max-w-xl sm:p-6">
          <h2
            className="mb-2 text-base font-semibold text-slate-950 sm:text-lg"
          >
            Cookies &amp; Datenschutz
          </h2>
          <p
            className="mb-4 text-xs leading-relaxed text-slate-700 sm:text-sm"
          >
            Wir verwenden technisch notwendige Cookies. Tracking- und Marketingdienste (z. B. Google Ads Conversion-Tracking für Telefonklicks) werden nur nach deiner ausdrücklichen Einwilligung geladen. 
            Details findest du in unserer&nbsp;
            <a href="/datenschutz" className="underline hover:no-underline">
              Datenschutzerklärung
            </a>
            &nbsp;und in den&nbsp;
            <a href="/cookie" className="underline hover:no-underline">
              Cookie-Einstellungen
            </a>
            .
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
            <button
              type="button"
              onClick={handleReject}
              className="w-full rounded-full border-2 border-[#0F3D8C] bg-white px-4 py-2.5 text-xs font-semibold text-[#0F3D8C] transition-colors hover:bg-[#EFF6FF] sm:w-auto sm:text-sm"
            >
              Nur notwendige Cookies
            </button>
            <button
              type="button"
              onClick={handleAccept}
              className="w-full rounded-full bg-[#0F3D8C] px-5 py-2.5 text-xs font-semibold text-white shadow-[0_18px_40px_rgba(15,61,140,0.45)] transition-colors hover:bg-[#0C316F] sm:w-auto sm:text-sm"
            >
              Alle akzeptieren
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Nach Entscheidung: kleines Widget unten rechts
  return (
    <button
      type="button"
      aria-label="Cookie-Einstellungen öffnen"
      onClick={() => setStatus('unset')}
      className="fixed bottom-[6.9rem] left-3 z-[9998] rounded-full border border-slate-300 bg-white px-3 py-1.5 text-[0.62rem] font-semibold text-slate-900 shadow-[0_14px_32px_rgba(15,23,42,0.22)] transition-colors hover:border-sky-300 hover:text-[#0F3D8C] sm:bottom-4 sm:left-auto sm:right-4 sm:px-4 sm:py-2.5 sm:text-xs"
    >
      Cookie-Einstellungen
    </button>
  );
}
